import { useEffect, useRef } from 'react'

const SPACING = 36
const DOT_RADIUS = 1.5
const INFLUENCE_RADIUS = 180
const MAX_SHIFT = 4

export default function DotGrid() {
  const wrapperRef = useRef(null)
  const canvasRef = useRef(null)
  const rafRef = useRef(null)
  const sizeRef = useRef({ width: 0, height: 0 })
  const mouseRef = useRef({ x: -9999, y: -9999 })

  useEffect(() => {
    const wrapper = wrapperRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    const resize = ({ width, height }) => {
      sizeRef.current = { width, height }
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
    }

    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      resize({ width, height })
    })
    ro.observe(wrapper)
    resize(wrapper.getBoundingClientRect())

    const onMouseMove = e => {
      const rect = wrapper.getBoundingClientRect()
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }
    const onMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 }
    }
    wrapper.addEventListener('mousemove', onMouseMove)
    wrapper.addEventListener('mouseleave', onMouseLeave)

    let startTime = null

    function draw(ts) {
      if (!startTime) startTime = ts
      const elapsed = (ts - startTime) / 1000
      const { width, height } = sizeRef.current
      const { x: mx, y: my } = mouseRef.current

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)

      const cols = Math.ceil(width / SPACING) + 1
      const rows = Math.ceil(height / SPACING) + 1

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const baseX = c * SPACING
          const baseY = r * SPACING

          const wave1 = Math.sin(c * 0.4 - elapsed * 0.6 + r * 0.2) * 0.5 + 0.5
          const wave2 = Math.sin(r * 0.35 + elapsed * 0.4 - c * 0.15) * 0.5 + 0.5
          const combined = wave1 * 0.6 + wave2 * 0.4

          // Mouse influence
          const dx = mx - baseX
          const dy = my - baseY
          const dist = Math.sqrt(dx * dx + dy * dy)
          const influence = Math.max(0, 1 - dist / INFLUENCE_RADIUS)
          const eased = influence * influence // quadratic falloff — smoother edge
          const shiftX = dist > 0 ? (dx / dist) * eased * MAX_SHIFT : 0
          const shiftY = dist > 0 ? (dy / dist) * eased * MAX_SHIFT : 0

          const opacity = 0.015 + combined * 0.07 + eased * 0.04
          const radius = DOT_RADIUS * (0.6 + combined * 0.7 + eased * 0.4)

          ctx.beginPath()
          ctx.arc(baseX + shiftX, baseY + shiftY, radius, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(255,255,255,${opacity.toFixed(3)})`
          ctx.fill()
        }
      }

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(rafRef.current)
      ro.disconnect()
      wrapper.removeEventListener('mousemove', onMouseMove)
      wrapper.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [])

  return (
    <div ref={wrapperRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  )
}
